import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSignupMutation } from '../redux/apiSlice'; 
import SignupPersonalInfo from '../components/SignupPersonalInfo';
import SignupProfessionalInfo from '../components/SignupProfessionalInfo';
import SignupPassword from '../components/SignupPassword';
import SignupVisualSection from '../components/SignupVisualSection';
import SignupHeader from '../components/SignupHeader';

const Signup = ({ onSignup }) => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personal: {
      firstName: '',
      lastName: '',
      email: '',
      profilePicture: '',
      phone: '',
      bio: ''
    },
    professional: {
      title: '',
      department: '',
      areaOfExpertise: '',
      experience: '',
      website: '',
      youtubeVideoUrl: '',
      cv: null
    },
    password: {
      newPassword: '',
      confirmPassword: ''
    },
    agreeToTerms: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();

  const [signup, { isLoading: isSignupLoading }] = useSignupMutation();

  useEffect(() => {
    setMounted(true);
    console.log('🚀 Signup component mounted');
    
    if (location.state?.step) {
      console.log('📍 Returning from policy page to step:', location.state.step);
      setCurrentStep(location.state.step);
      if (location.state.formData) {
        console.log('📦 Restoring form data from state');
        setFormData(location.state.formData);
      }
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const handleFileUpload = (file) => {
    if (!file) {
      setFormData(prev => ({
        ...prev,
        personal: { ...prev.personal, profilePicture: '' }
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        'personal.profilePicture': 'File size must be less than 5MB'
      }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        'personal.profilePicture': 'Please upload an image file (PNG, JPG, JPEG)'
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        personal: { ...prev.personal, profilePicture: e.target.result }
      }));
      if (errors['personal.profilePicture']) {
        setErrors(prev => ({ ...prev, 'personal.profilePicture': '' }));
      }
    };
    reader.readAsDataURL(file);
  };

  const convertExperienceToNumber = (experienceString) => {
    switch (experienceString) {
      case '0-2': return 1;
      case '3-5': return 4;
      case '6-10': return 8;
      case '10+': return 11;
      default: return 0;
    }
  };

  const base64ToFile = (base64String, filename) => {
    const arr = base64String.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.personal.firstName.trim()) {
        newErrors['personal.firstName'] = 'First name is required';
      }
      if (!formData.personal.lastName.trim()) {
        newErrors['personal.lastName'] = 'Last name is required';
      }
      if (!formData.personal.email) {
        newErrors['personal.email'] = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.personal.email)) {
        newErrors['personal.email'] = 'Email is invalid';
      }
      if (!formData.personal.phone.trim()) {
        newErrors['personal.phone'] = 'Phone number is required';
      }
    }

    if (step === 2) {
      if (!formData.professional.department.trim()) {
        newErrors['professional.department'] = 'Subjects are required';
      }
      if (!formData.professional.areaOfExpertise.trim()) {
        newErrors['professional.areaOfExpertise'] = 'Area of expertise is required';
      }
      if (!formData.professional.experience) {
        newErrors['professional.experience'] = 'Experience is required';
      }
      if (!formData.professional.cv) {
        newErrors['professional.cv'] = 'CV is required';
      }
    }

    if (step === 3) {
      if (!formData.password.newPassword) {
        newErrors['password.newPassword'] = 'Password is required';
      } else if (formData.password.newPassword.length < 8) {
        newErrors['password.newPassword'] = 'Password must be at least 8 characters';
      }
      if (!formData.password.confirmPassword) {
        newErrors['password.confirmPassword'] = 'Please confirm your password';
      } else if (formData.password.newPassword !== formData.password.confirmPassword) {
        newErrors['password.confirmPassword'] = 'Passwords do not match';
      }
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      console.log('❌ Form validation failed');
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      const submitData = new FormData();

      // ===== BASIC FIELDS =====
      submitData.append('fname', formData.personal.firstName.trim());
      submitData.append('lname', formData.personal.lastName.trim());
      submitData.append('email', formData.personal.email.trim());
      submitData.append('phoneNum', formData.personal.phone.trim());
      submitData.append('password', formData.password.newPassword);
      
      // Optional fields
      if (formData.personal.bio.trim()) {
        submitData.append('bio', formData.personal.bio.trim());
      }
      
      // ===== PROFESSIONAL TITLE =====
      if (formData.professional.title.trim()) {
        submitData.append('proftitle', formData.professional.title.trim());
      }

      // ===== WEBSITE =====
      if (formData.professional.website.trim()) {
        submitData.append('website', formData.professional.website.trim());
      }

      // ===== VIDEO URL =====
      if (formData.professional.youtubeVideoUrl.trim()) {
        submitData.append('videoUrl', formData.professional.youtubeVideoUrl.trim());
      }

      // ===== CRITICAL FIX: Send department as SINGLE comma-separated string =====
      // Exactly like your curl example: -F 'department=Sociology,Current Affairs'
      if (formData.professional.department.trim()) {
        submitData.append('department', formData.professional.department.trim());
        console.log(`📤 Appending department: ${formData.professional.department.trim()}`);
      }

      // ===== CRITICAL FIX: Send expertise as SINGLE comma-separated string =====
      // Exactly like your curl example: -F 'expertise=PMS,SPSC'
      if (formData.professional.areaOfExpertise.trim()) {
        submitData.append('expertise', formData.professional.areaOfExpertise.trim());
        console.log(`📤 Appending expertise: ${formData.professional.areaOfExpertise.trim()}`);
      }

      // ===== YEARS OF EXPERIENCE =====
      submitData.append('yearsOfExperience', convertExperienceToNumber(formData.professional.experience).toString());

      // ===== PROFILE PICTURE (optional) =====
      if (formData.personal.profilePicture) {
        const imageFile = base64ToFile(formData.personal.profilePicture, 'profile-picture.jpg');
        submitData.append('image', imageFile);
      } else {
        submitData.append('image', ''); // Send empty string if no image
      }

      // ===== CV FILE (mandatory) =====
      if (formData.professional.cv) {
        submitData.append('cv', formData.professional.cv);
        console.log(`📤 Appending cv: ${formData.professional.cv.name} (${formData.professional.cv.size} bytes)`);
      }

      // Debug logging - show all form data
      console.log('📤 Sending API request with following data:');
      console.log('📍 API Endpoint: /auth/signup');
      console.log('📝 Form Data Entries:');
      
      for (let [key, value] of submitData.entries()) {
        if (key === 'image' && value instanceof File) {
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
        } else if (key === 'cv' && value instanceof File) {
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }

      // Verify department and expertise are sent as single strings
      const departmentValue = submitData.get('department');
      const expertiseValue = submitData.get('expertise');
      console.log(`✅ Department value: "${departmentValue}"`);
      console.log(`✅ Expertise value: "${expertiseValue}"`);

      // Call the API
      console.log('🔄 Making API call...');
      const response = await signup(submitData).unwrap();
      
      console.log('✅ API Response Received:', response);

      if (response.status === true) {
        console.log('🎉 Signup successful! Redirecting...');
        if (onSignup) {
          onSignup(response.data);
        }
        navigate('/signup-success', { 
          replace: true,
          state: { userData: response.data }
        });
      } else {
        console.warn('⚠️ API returned status false:', response);
        setApiError(response.message || 'Signup failed. Please try again.');
      }

    } catch (error) {
      console.error('❌ Signup error:', error);
      
      if (error.data) {
        setApiError(error.data.message || 'Signup failed. Please check your information and try again.');
      } else if (error.status) {
        switch (error.status) {
          case 400:
            setApiError('Invalid data provided. Please check your information.');
            break;
          case 409:
            setApiError('Email already exists. Please use a different email.');
            break;
          case 500:
            setApiError('Server error. Please try again later.');
            break;
          default:
            setApiError('Signup failed. Please try again.');
        }
      } else {
        setApiError('Network error. Please check your connection and try again.');
      }
    } finally {
      console.log('🏁 API call completed');
      setIsLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <SignupPersonalInfo
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            nextStep={nextStep}
            handleFileUpload={handleFileUpload}
          />
        );
      case 2:
        return (
          <SignupProfessionalInfo
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        );
      case 3:
        return (
          <SignupPassword
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            isLoading={isLoading || isSignupLoading}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <SignupVisualSection mounted={mounted} currentStep={currentStep} />

      <div className="flex-1 flex flex-col justify-center py-8 lg:py-12 px-6 sm:px-8 lg:px-12 bg-white order-1 lg:order-2">
        <div className="w-full max-w-md mx-auto">
          <SignupHeader mounted={mounted} currentStep={currentStep} />

          {apiError && (
            <div className={`mb-6 p-4 bg-red-50 border border-red-200 rounded-xl transform transition-all duration-500 ease-out ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm font-medium">{apiError}</p>
              </div>
            </div>
          )}

          <div className={`transform transition-all duration-800 ease-out delay-300 ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}>
            <form onSubmit={handleSubmit}>
              {renderCurrentStep()}
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 hover:underline">
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;